import { useEffect, useMemo, useState } from "react";
import { Search, Plus, Minus, Trash2, Banknote, QrCode, CreditCard, Wallet, PackageCheck, Percent } from "lucide-react";
import { toast } from "sonner";
import api, { peso, fmtErr } from "../../lib/api";
import ReceiptModal from "../../components/ReceiptModal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";

const METHODS = [
  { id: "cash", label: "Cash", icon: Banknote, cls: "text-[#0E7C84]" },
  { id: "gcash", label: "GCash", icon: QrCode, cls: "text-[#0052FF]" },
  { id: "card", label: "Card", icon: CreditCard, cls: "text-slate-700" },
  { id: "store_credit", label: "Store Credit", icon: Wallet, cls: "text-[#2A9D8F]" },
];

export default function POS() {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState("walkin");
  const [discountType, setDiscountType] = useState("none");
  const [promoAmount, setPromoAmount] = useState("");
  const [seniorId, setSeniorId] = useState("");
  const [payments, setPayments] = useState([]);
  const [receiptId, setReceiptId] = useState(null);
  const [onlineOrders, setOnlineOrders] = useState([]);
  const [charging, setCharging] = useState(false);

  const loadProducts = () => api.get("/pos/lookup", { params: { q: query } }).then((r) => setProducts(r.data)).catch((e) => toast.error(fmtErr(e)));
  const loadOnline = () => api.get("/sales", { params: { channel: "online", fulfillment: "pending_pickup" } }).then((r) => setOnlineOrders(r.data)).catch(() => {});

  useEffect(() => { loadProducts(); }, [query]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    loadOnline();
  }, []);

  const gross = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const totals = useMemo(() => {
    let vat = 0, vatable = 0, exempt = 0, discount = 0, total = gross;
    if (discountType === "senior" || discountType === "pwd") {
      exempt = gross / 1.12; discount = exempt * 0.2; total = exempt - discount;
    } else if (discountType === "promo") {
      discount = parseFloat(promoAmount) || 0; total = Math.max(0, gross - discount);
      vatable = total / 1.12; vat = total - vatable;
    } else {
      vatable = gross / 1.12; vat = gross - vatable;
    }
    const r = (x) => Math.round(x * 100) / 100;
    return { vat: r(vat), vatable: r(vatable), exempt: r(exempt), discount: r(discount), total: r(total) };
  }, [gross, discountType, promoAmount]);

  const paid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const remaining = Math.max(0, totals.total - paid);
  const change = Math.max(0, paid - totals.total);

  const addToCart = (p, variant = null) => {
    setCart((c) => {
      const key = p.id + (variant || "");
      const ex = c.find((x) => x.key === key);
      if (ex) return c.map((x) => x.key === key ? { ...x, qty: Math.min(x.qty + 1, p.stock) } : x);
      return [...c, { key, product_id: p.id, name: p.name + (variant ? ` (${variant})` : ""), variant, price: p.price, qty: 1, stock: p.stock }];
    });
  };
  const setQty = (key, d) => setCart((c) => c.map((x) => x.key === key ? { ...x, qty: Math.max(1, Math.min(x.qty + d, x.stock)) } : x).filter((x) => x.qty > 0));
  const removeLine = (key) => setCart((c) => c.filter((x) => x.key !== key));

  const addPayment = (method) => {
    if (payments.find((p) => p.method === method)) return;
    setPayments((p) => [...p, { method, amount: remaining ? remaining.toFixed(2) : totals.total.toFixed(2), reference: "" }]);
  };

  const charge = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    // No manual amount entry required: any unpaid remainder auto-settles as Cash
    const finalPayments = payments.map((p) => ({ ...p, amount: parseFloat(p.amount) || 0 }));
    const paidSum = finalPayments.reduce((s, p) => s + p.amount, 0);
    if (paidSum + 0.005 < totals.total) {
      const short = Math.round((totals.total - paidSum) * 100) / 100;
      const cashRow = finalPayments.find((p) => p.method === "cash");
      if (cashRow) cashRow.amount = Math.round((cashRow.amount + short) * 100) / 100;
      else finalPayments.push({ method: "cash", amount: short, reference: "" });
    }
    setCharging(true);
    try {
      const { data } = await api.post("/sales", {
        items: cart.map((c) => ({ product_id: c.product_id, qty: c.qty, variant: c.variant })),
        customer_id: customerId === "walkin" ? null : customerId,
        discount_type: discountType,
        promo_amount: discountType === "promo" ? parseFloat(promoAmount) || 0 : 0,
        senior_id: ["senior", "pwd"].includes(discountType) ? seniorId : null,
        payments: finalPayments.map((p) => ({ method: p.method, amount: p.amount, reference: p.reference || null })),
        channel: "pos", fulfillment: "completed",
      });
      toast.success(`Sale ${data.receipt_no} completed — change ${peso(data.change)}`);
      setReceiptId(data.id);
      setCart([]); setPayments([]); setDiscountType("none"); setPromoAmount(""); setSeniorId(""); setCustomerId("walkin");
      loadProducts();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setCharging(false);
    }
  };

  const fulfill = async (id) => {
    try { await api.post(`/sales/${id}/fulfill`); toast.success("Marked as picked up"); loadOnline(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="animate-rise">
      <Tabs defaultValue="register">
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="pos-title">POS Register</h1>
          <TabsList className="bg-[#E2F5F5]">
            <TabsTrigger value="register" data-testid="pos-tab-register">Register</TabsTrigger>
            <TabsTrigger value="online" data-testid="pos-tab-online">
              Online Orders {onlineOrders.length > 0 && <Badge className="ml-1 bg-[#E84855] hover:bg-[#E84855] text-[10px]">{onlineOrders.length}</Badge>}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="register">
          <div className="grid lg:grid-cols-5 gap-5">
            <div className="lg:col-span-3 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input className="pl-9 bg-white" placeholder="Search product name or scan SKU…" value={query}
                  onChange={(e) => setQuery(e.target.value)} data-testid="pos-search" autoFocus />
              </div>
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 max-h-[62vh] overflow-y-auto pr-1">
                {products.map((p) => (
                  <div key={p.id} className="bg-white rounded-xl border border-[#BFE8E9] p-3 hover-lift" data-testid={`pos-product-${p.sku}`}>
                    <p className="font-semibold text-sm leading-snug min-h-[2.4rem]">{p.name}</p>
                    <p className="font-mono text-[10px] text-slate-400">{p.sku}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="font-display font-bold text-[#0E7C84]">{peso(p.price)}</span>
                      <span className={`text-[11px] ${p.stock <= p.low_stock_threshold ? "text-amber-600 font-semibold" : "text-slate-400"}`}>{p.stock} left</span>
                    </div>
                    {p.variants?.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {p.variants.map((v) => (
                          <button key={v} onClick={() => addToCart(p, v)} className="text-[10px] px-2 py-1 rounded-md bg-[#D9F7F8] text-[#0E7C84] font-medium hover:bg-[#0E7C84] hover:text-white transition-colors" data-testid={`pos-variant-${p.sku}-${v.toLowerCase()}`}>
                            + {v}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="w-full mt-2 h-8" onClick={() => addToCart(p)} data-testid={`pos-add-${p.sku}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add
                      </Button>
                    )}
                  </div>
                ))}
                {products.length === 0 && <p className="col-span-full text-center text-sm text-slate-400 py-10">No products found.</p>}
              </div>
            </div>

            <div className="lg:col-span-2 bg-white rounded-2xl border border-[#BFE8E9] p-4 flex flex-col max-h-[78vh]" data-testid="pos-bill-panel">
              <div className="mb-3">
                <Label className="text-xs">Customer</Label>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger className="bg-white" data-testid="pos-customer-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="walkin">Walk-in Customer</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.loyalty_points > 0 ? `(${c.loyalty_points} pts)` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 overflow-y-auto space-y-2 min-h-[100px]">
                {cart.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Scan or tap products to add</p>}
                {cart.map((c) => (
                  <div key={c.key} className="flex items-center gap-2 text-sm border-b border-[#E2F5F5] pb-2" data-testid={`cart-line-${c.key.slice(0, 8)}`}>
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{c.name}</p>
                      <p className="text-xs text-slate-400">{peso(c.price)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button className="w-6 h-6 rounded bg-[#E2F5F5] flex items-center justify-center" onClick={() => setQty(c.key, -1)}><Minus className="w-3 h-3" /></button>
                      <span className="w-6 text-center font-semibold">{c.qty}</span>
                      <button className="w-6 h-6 rounded bg-[#E2F5F5] flex items-center justify-center" onClick={() => setQty(c.key, 1)}><Plus className="w-3 h-3" /></button>
                    </div>
                    <span className="w-16 text-right font-semibold">{peso(c.price * c.qty)}</span>
                    <button className="text-slate-300 hover:text-red-500" onClick={() => removeLine(c.key)}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#BFE8E9] pt-3 mt-2 space-y-3">
                <div>
                  <Label className="text-xs mb-1.5 block">Discount</Label>
                  <RadioGroup value={discountType} onValueChange={setDiscountType} className="grid grid-cols-4 gap-1.5">
                    {[["none", "None"], ["promo", "Promo"], ["senior", "Senior"], ["pwd", "PWD"]].map(([v, l]) => (
                      <label key={v} className={`flex items-center justify-center gap-1 border rounded-lg py-1.5 text-xs font-medium cursor-pointer transition-colors ${discountType === v ? "border-[#E84855] bg-orange-50 text-[#C62836]" : ""}`} data-testid={`discount-${v}`}>
                        <RadioGroupItem value={v} className="sr-only" />{l}
                      </label>
                    ))}
                  </RadioGroup>
                  {discountType === "promo" && (
                    <Input className="mt-2" type="number" placeholder="Promo amount (₱)" value={promoAmount} onChange={(e) => setPromoAmount(e.target.value)} data-testid="promo-amount" />
                  )}
                  {["senior", "pwd"].includes(discountType) && (
                    <Input className="mt-2" placeholder="Senior/PWD ID number" value={seniorId} onChange={(e) => setSeniorId(e.target.value)} data-testid="senior-id-input" />
                  )}
                </div>

                <div className="bg-[#E2F5F5] rounded-xl p-3 text-sm space-y-1 font-mono">
                  <div className="flex justify-between"><span>Subtotal</span><span data-testid="pos-subtotal">{peso(gross)}</span></div>
                  {totals.discount > 0 && <div className="flex justify-between text-[#C62836]"><span>Discount</span><span>-{peso(totals.discount)}</span></div>}
                  <div className="flex justify-between text-slate-500"><span>VAT (12%)</span><span>{peso(totals.vat)}</span></div>
                  {totals.exempt > 0 && <div className="flex justify-between text-slate-500"><span>VAT-Exempt</span><span>{peso(totals.exempt)}</span></div>}
                  <div className="flex justify-between font-bold text-base border-t border-[#BFE8E9] pt-1"><span>TOTAL</span><span data-testid="pos-total">{peso(totals.total)}</span></div>
                </div>

                <div>
                  <Label className="text-xs mb-1.5 block">Payments (optional — balance auto-charges as Cash)</Label>
                  <div className="grid grid-cols-4 gap-1.5 mb-2">
                    {METHODS.map((m) => (
                      <button key={m.id} onClick={() => addPayment(m.id)} data-testid={`pay-method-${m.id}`}
                        className={`flex flex-col items-center gap-0.5 border rounded-lg py-2 text-[11px] font-semibold transition-colors hover:border-[#0E7C84] ${payments.find((p) => p.method === m.id) ? "bg-[#D9F7F8] border-[#0E7C84]" : ""}`}>
                        <m.icon className={`w-4 h-4 ${m.cls}`} />{m.label}
                      </button>
                    ))}
                  </div>
                  {payments.map((p) => (
                    <div key={p.method} className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-semibold w-20 capitalize">{p.method.replace("_", " ")}</span>
                      <Input type="number" className="h-8 flex-1" value={p.amount} data-testid={`pay-amount-${p.method}`}
                        onChange={(e) => setPayments((ps) => ps.map((x) => x.method === p.method ? { ...x, amount: e.target.value } : x))} />
                      {(p.method === "gcash" || p.method === "card") && (
                        <Input className="h-8 flex-1" placeholder="Ref no." value={p.reference} data-testid={`pay-ref-${p.method}`}
                          onChange={(e) => setPayments((ps) => ps.map((x) => x.method === p.method ? { ...x, reference: e.target.value } : x))} />
                      )}
                      <button className="text-slate-300 hover:text-red-500" onClick={() => setPayments((ps) => ps.filter((x) => x.method !== p.method))}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {payments.length > 0 && (
                    <div className="flex justify-between text-xs font-mono mt-1">
                      <span className={remaining > 0 ? "text-amber-600" : "text-emerald-600"} data-testid="pos-remaining">
                        {remaining > 0 ? `Remaining: ${peso(remaining)}` : `Change: ${peso(change)}`}
                      </span>
                      <span>Paid: {peso(paid)}</span>
                    </div>
                  )}
                </div>

                <Button className="w-full bg-[#0E7C84] hover:bg-[#093F46] h-11 text-base" onClick={charge} disabled={charging || cart.length === 0} data-testid="charge-btn">
                  {charging ? "Processing…" : `Charge ${peso(totals.total)}`}
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="online">
          <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="online-orders-panel">
            <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><PackageCheck className="w-4 h-4 text-[#E84855]" /> Online orders awaiting pickup</h3>
            {onlineOrders.length === 0 && <p className="text-sm text-slate-500">No pending online orders.</p>}
            <div className="space-y-2">
              {onlineOrders.map((o) => (
                <div key={o.id} className="flex flex-wrap items-center justify-between gap-3 border border-[#E2F5F5] rounded-xl p-3" data-testid={`online-order-${o.receipt_no}`}>
                  <div>
                    <p className="font-mono font-semibold text-sm">{o.receipt_no}</p>
                    <p className="text-xs text-slate-500">{o.customer_name} · {o.items.map((i) => `${i.name} x${i.qty}`).join(", ")}</p>
                    <p className="text-xs text-slate-500 capitalize">Paid via {o.payments.map((p) => p.method).join(" + ")}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-display font-bold text-[#0E7C84]">{peso(o.total)}</span>
                    <Button size="sm" className="bg-[#2A9D8F] hover:bg-[#238b7e]" onClick={() => fulfill(o.id)} data-testid={`fulfill-${o.receipt_no}`}>Mark picked up</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <ReceiptModal saleId={receiptId} open={!!receiptId} onClose={() => setReceiptId(null)} allowEmail />
    </div>
  );
}
